import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  LogOut,
  BookOpen,
  History,
  Clock,
  Star,
  Bell,
  LayoutGrid,
  UserCircle,
  Settings,
  ShieldCheck,
  HelpCircle,
  BarChart3,
} from "lucide-react";
import { useAuth } from "../App";

// Dashboard-specific hamburger menu — richer than the shared AppMenu:
//   • Shows live counts (Completed / In Progress / Favorites / Reminders)
//   • Has a "Reminders" row that opens the Dashboard's reminders dialog
//     via onOpenReminders() rather than navigating away
//
// Uses the same hand-rolled dropdown pattern as AppMenu (no Radix). Radix
// DropdownMenu became unresponsive on iPadOS 26.5 during App Review — the
// new iPadOS pointer model doesn't reliably deliver pointer events to
// Radix's popover portal. Plain onClick + onTouchStart with a ref-based
// outside-click handler works everywhere (iOS, iPadOS, macOS, web).

export const DashboardMenu = ({ stats, remindersCount = 0, onOpenReminders }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handle = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    document.addEventListener("touchstart", handle);
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("touchstart", handle);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // setTimeout 0 avoids React reconciler conflicts on rapid touch events
  // (same trick used in AppMenu).
  const go = useCallback((path) => {
    setOpen(false);
    setTimeout(() => navigate(path), 0);
  }, [navigate]);

  const handleLogout = useCallback(() => {
    setOpen(false);
    setTimeout(() => logout(), 0);
  }, [logout]);

  const handleOpenReminders = useCallback(() => {
    setOpen(false);
    if (onOpenReminders) setTimeout(() => onOpenReminders(), 0);
  }, [onOpenReminders]);

  const itemBase =
    "w-full px-3 py-2 text-left text-sm text-[#1B2349] hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2";
  const itemStyle = { touchAction: "manipulation", WebkitTapHighlightColor: "transparent" };

  return (
    <div ref={containerRef} className="relative" style={{ touchAction: "manipulation" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-10 h-10 rounded-full hover:bg-slate-100 active:bg-slate-200 flex items-center justify-center transition-colors"
        style={itemStyle}
        data-testid="user-menu-btn"
        aria-label="Open menu"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Menu className="w-5 h-5 text-[#1B2349]" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 rounded-xl bg-white shadow-lg ring-1 ring-black/5 overflow-hidden z-50 py-1"
          data-testid="user-menu-content"
        >
          {user && (
            <div className="px-3 py-2 border-b border-slate-100">
              <p className="text-sm font-medium text-[#1B2349] truncate">{user.name}</p>
              <p className="text-xs text-[#718096] truncate">{user.email}</p>
            </div>
          )}

          <button
            type="button"
            onClick={() => go("/getting-started")}
            role="menuitem"
            className={itemBase}
            style={itemStyle}
            data-testid="menu-getting-started"
          >
            <BookOpen className="w-4 h-4" />
            Getting Started
          </button>

          <div className="my-1 border-t border-slate-100" />

          <button
            type="button"
            onClick={() => go("/history")}
            role="menuitem"
            className={`${itemBase} justify-between`}
            style={itemStyle}
            data-testid="menu-completed"
          >
            <span className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Completed
            </span>
            <span
              className="ml-3 text-xs font-semibold text-[#3090A8] tabular-nums"
              data-testid="menu-completed-count"
            >
              {stats?.completed_interventions || 0}
            </span>
          </button>

          <button
            type="button"
            onClick={() => go("/dashboard")}
            role="menuitem"
            className={`${itemBase} justify-between`}
            style={itemStyle}
            data-testid="menu-in-progress"
          >
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              In Progress
            </span>
            <span
              className="ml-3 text-xs font-semibold text-[#78C0D8] tabular-nums"
              data-testid="menu-in-progress-count"
            >
              {stats?.in_progress_count || 0}
            </span>
          </button>

          <button
            type="button"
            onClick={() => go("/favorites")}
            role="menuitem"
            className={`${itemBase} justify-between`}
            style={itemStyle}
            data-testid="menu-favorites"
          >
            <span className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              Favorites
            </span>
            <span
              className="ml-3 text-xs font-semibold text-[#E2B887] tabular-nums"
              data-testid="menu-favorites-count"
            >
              {stats?.favorites_count || 0}
            </span>
          </button>

          <button
            type="button"
            onClick={handleOpenReminders}
            role="menuitem"
            className={`${itemBase} justify-between`}
            style={itemStyle}
            data-testid="menu-reminders"
          >
            <span className="flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Reminders
            </span>
            <span
              className="ml-3 text-xs font-semibold text-[#E2B887] tabular-nums"
              data-testid="menu-reminders-count"
            >
              {remindersCount}
            </span>
          </button>

          <div className="my-1 border-t border-slate-100" />

          <button
            type="button"
            onClick={() => go("/practices")}
            role="menuitem"
            className={itemBase}
            style={itemStyle}
            data-testid="menu-practices"
          >
            <LayoutGrid className="w-4 h-4" />
            Practice Library
          </button>
          <button
            type="button"
            onClick={() => go("/account")}
            role="menuitem"
            className={itemBase}
            style={itemStyle}
            data-testid="menu-account"
          >
            <UserCircle className="w-4 h-4" />
            Account
          </button>
          <button
            type="button"
            onClick={() => go("/settings")}
            role="menuitem"
            className={itemBase}
            style={itemStyle}
            data-testid="menu-settings"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
          <button
            type="button"
            onClick={() => go("/disclaimer")}
            role="menuitem"
            className={itemBase}
            style={itemStyle}
            data-testid="menu-disclaimer"
          >
            <ShieldCheck className="w-4 h-4" />
            Disclaimer
          </button>
          <button
            type="button"
            onClick={() => go("/faq")}
            role="menuitem"
            className={itemBase}
            style={itemStyle}
            data-testid="menu-faq"
          >
            <HelpCircle className="w-4 h-4" />
            FAQ
          </button>

          {user?.role === "admin" && (
            <button
              type="button"
              onClick={() => go("/admin")}
              role="menuitem"
              className={itemBase}
              style={itemStyle}
              data-testid="menu-analytics"
            >
              <BarChart3 className="w-4 h-4" />
              Analytics
            </button>
          )}

          <div className="my-1 border-t border-slate-100" />
          <button
            type="button"
            onClick={handleLogout}
            role="menuitem"
            className="w-full px-3 py-2 text-left text-sm text-[#D98880] hover:bg-slate-50 active:bg-slate-100 flex items-center gap-2"
            style={itemStyle}
            data-testid="menu-logout"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

export default DashboardMenu;
